// CharacterSheet Component
// RPG-style character stats design showcasing skills with experience bars
// Based on your Bento profile: Web3 consultant, GameFI specialist, BD, etc.

import React, { useState, useEffect, useRef } from 'react';
import './CharacterSheet.css';
import profileVideo from '../assets/dd-profile-1.mp4';
import skillsData from '../data/skills.json';

function CharacterSheet() {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const videoRef = useRef(null);

  // Skills array from JSON data file with fallback
  const skills = skillsData?.skills || [];

  // Debug: Log to verify data is loading correctly
  useEffect(() => {
    if (!skillsData || !skillsData.skills) {
      console.error('Skills data not loaded correctly:', skillsData);
    }
  }, []);

  // Ensure video loops continuously
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    
    // Ensure video loops and plays
    const handleLoadedMetadata = () => {
      if (video) {
        video.loop = true;
        video.play().catch(err => {
          // Auto-play may be blocked by browser, but looping will still work
          console.log('Video autoplay prevented:', err);
        });
      }
    };

    // Fallback handler: restart video if loop attribute doesn't work
    const handleEnded = () => {
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    };

    // Set loop attribute and ensure it's enabled
    video.loop = true;
    
    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);

    return () => {
      if (video) {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('ended', handleEnded);
      }
    };
  }, []);

  return (
    <section id="character-sheet" className="expertise">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Character sheet</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            8 years of hands-on experience in the blockchain industry.
          </p>
          <p className="section-subtitle" style={{ marginBottom: '0.5rem' }}>
            Here's what I bring to the table for your project.
          </p>
          <p className="character-quote">
            Improve 1% every single day. And you'll outperform 99% of people.
          </p>
        </div>

        {/* Character Stats Layout */}
        <div className="character-stats-container">
          {/* Left side - Character Portrait */}
          <div className="character-portrait">
            <div className="portrait-frame">
              <video 
                ref={videoRef}
                src={profileVideo} 
                className="portrait-image"
                autoPlay
                loop
                muted
                playsInline
                aria-label="Character Profile"
              />
            </div>
          </div>

          {/* Right side - Stats/Skills */}
          <div className="character-skills">
            {skills && skills.length > 0 ? skills.map((skill, index) => {
              // Safety check: ensure skill is an object with required properties
              if (!skill || typeof skill !== 'object') {
                return null;
              }
              
              // Ensure index is a number
              const skillIndex = typeof index === 'number' ? index : -1;
              const isHovered = hoveredSkill === skillIndex;
              const rowClassName = `skill-row${isHovered ? ' tooltip-active' : ''}`;
              
              // Handle click to toggle tooltip (useful for mobile)
              const handleClick = () => {
                if (isHovered) {
                  // If already open, close it
                  setHoveredSkill(null);
                } else {
                  // If closed, open it
                  setHoveredSkill(skillIndex);
                }
              };
              
              return (
                <div 
                  key={skillIndex} 
                  className={rowClassName}
                  onMouseEnter={() => setHoveredSkill(skillIndex)}
                  onMouseLeave={() => setHoveredSkill(null)}
                  onClick={handleClick}
                >
                  <div className="skill-info">
                    <span className="skill-icon">
                      {skill.iconClass && typeof skill.iconClass === 'string' ? (
                        <i className={skill.iconClass}></i>
                      ) : (
                        <span>{typeof skill.icon === 'string' ? skill.icon : ''}</span>
                      )}
                    </span>
                    <span className="skill-name">{typeof skill.name === 'string' ? skill.name : 'Unknown Skill'}</span>
                    {skill.description && typeof skill.description === 'string' && (
                      <span className="skill-interactive-hint">
                        <i className="fas fa-info-circle"></i>
                      </span>
                    )}
                  </div>
                  <div className="exp-bar-container">
                    {Array.from({ length: 20 }, (_, segmentIndex) => {
                      const segmentPercent = (segmentIndex + 1) * 5;
                      const skillLevel = typeof skill.level === 'number' ? skill.level : 0;
                      const isFilled = skillLevel >= segmentPercent;
                      const segmentClassName = `exp-bar-segment ${isFilled ? 'filled' : 'empty'}`;
                      return (
                        <div
                          key={segmentIndex}
                          className={segmentClassName}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Hover tooltip */}
                  {isHovered && skill.description && typeof skill.description === 'string' && (
                    <div className="skill-tooltip">
                      {skill.description}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="no-skills">No skills data available</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CharacterSheet;

